import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import AppActions from 'app/shared/flux/actions/AppActions';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import NewWindowTealIcon from 'images/icons/new-window-teal.svg';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanel } from '../../../ui/DetailPanel';
import { MiniMap } from '../../../ui/MiniMap';
import { NeighborhoodSpoke, NeighborhoodSpokeContent } from '../NeighborhoodModule/NeighborhoodSpoke';
import { NeighborhoodProvider } from '../NeighborhoodModule/context';
import { DesktopCTAs } from './DesktopCTAs';
import { getListingSummaryDisplay } from './listingSummaryModule.utils';
import {
  addressText,
  addressTriggerRow,
  buildingName,
  container,
  detailsRow,
  externalIcon,
  priceRow,
  priceSuffix,
  priceText,
  websiteContainer,
  websiteLink,
} from './listingSummaryModule.styles';

const AddressTriggerContent = ({
  addressDisplay,
  lat,
  lon,
}: {
  addressDisplay: string;
  lat?: number;
  lon?: number;
}) => (
  <>
    {lat != null && lon != null && <MiniMap lat={lat} lon={lon} />}
    <span className={addressText}>{addressDisplay}</span>
  </>
);

export const ListingSummaryModule = () => {
  const dispatch = useDispatch();
  const { openPanel, isMobile } = useDetailPanel();
  const currentListing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  if (!currentListing) {
    return null;
  }

  const {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    unitCount,
    showUnits,
    roomSqFt,
    showRoomSqFt,
    roommatesCount,
    showHousemates,
    housematesLabel,
    addressDisplay,
    websiteUrl,
    websiteLinkText,
  } = getListingSummaryDisplay(currentListing);

  const geo = currentListing.geo;
  const address = currentListing.address;

  const handleWebsiteClick = () => {
    dispatch(AppActions.sendEventToApi('building_website_visited', null));
    if (currentListing.maloneLotIdEncoded) {
      dispatch(AppActions.sendEventToApi('building_website_clicked', currentListing.maloneLotIdEncoded));
    }
  };

  const handleOpenNeighborhood = () => {
    openPanel('neighborhood', NeighborhoodSpokeContent, { provider: NeighborhoodProvider });
  };

  const renderAddressTrigger = () => {
    if (!address) {
      return null;
    }

    const triggerContent = <AddressTriggerContent addressDisplay={addressDisplay} lat={geo?.lat} lon={geo?.lon} />;

    if (!isMobile) {
      return (
        <button
          type="button"
          className={addressTriggerRow}
          onClick={handleOpenNeighborhood}
          aria-label={`View neighborhood for ${addressDisplay}`}
        >
          {triggerContent}
        </button>
      );
    }

    return (
      <FullscreenOverlay.Root defaultOpen={false}>
        <FullscreenOverlay.Trigger asChild>
          <button type="button" className={addressTriggerRow} aria-label={`View neighborhood for ${addressDisplay}`}>
            {triggerContent}
          </button>
        </FullscreenOverlay.Trigger>

        <FullscreenOverlay.Portal>
          <FullscreenOverlay.Backdrop>
            <FullscreenOverlay.Content>
              <NeighborhoodSpoke />
            </FullscreenOverlay.Content>
          </FullscreenOverlay.Backdrop>
        </FullscreenOverlay.Portal>
      </FullscreenOverlay.Root>
    );
  };

  return (
    <div className={container}>
      {!shouldHideDetails && (
        <>
          {showPrice && (
            <div className={priceRow}>
              <span className={priceText}>{priceDisplay}</span>
              <span className={priceSuffix}>{'/ mo'}</span>
              {shouldShowTotalMonthlyPriceBadge(currentListing) && (
                <TotalMonthlyPriceBadge listing={currentListing} iconVariant="default" />
              )}
            </div>
          )}

          {(showBedRange || showBathRange || showUnits || showRoomSqFt || showHousemates) && (
            <div className={detailsRow}>
              {showBedRange && <span>{bedRangeDisplay}</span>}
              {showBedRange && showBathRange && <span> | </span>}
              {showBathRange && <span>{bathRangeDisplay}</span>}
              {(showBedRange || showBathRange) && showUnits && <span> | </span>}
              {showUnits && <span>{unitCount} units available</span>}
              {(showBedRange || showBathRange || showUnits) && showRoomSqFt && <span> | </span>}
              {showRoomSqFt && <span>{roomSqFt} sqft room</span>}
              {(showBedRange || showBathRange || showUnits || showRoomSqFt) && showHousemates && <span> | </span>}
              {showHousemates && (
                <span>
                  {roommatesCount} {housematesLabel}
                </span>
              )}
            </div>
          )}
        </>
      )}

      {currentListing.displayName && <div className={buildingName}>{currentListing.displayName}</div>}

      {websiteUrl && (
        <div className={websiteContainer}>
          <a
            href={websiteUrl}
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={handleWebsiteClick}
            className={websiteLink}
          >
            {websiteLinkText}
            <img src={NewWindowTealIcon} alt="" className={externalIcon} aria-hidden="true" />
          </a>
        </div>
      )}

      {renderAddressTrigger()}

      <DesktopCTAs />
    </div>
  );
};
